/**
 * @file status-bar-coordinator.ts
 * @description Handles the drawing status bar updates, mouse position tracking, and input synchronization.
 */

import { g, Tool } from '@hcie/core';
import { EventBus } from '@hcie/shared';

// DOM Element Referansları
const drawingInfo = document.getElementById('drawingInfo');
const startXInput = document.getElementById('startXInput') as HTMLInputElement | null;
const startYInput = document.getElementById('startYInput') as HTMLInputElement | null;
const widthInput = document.getElementById('widthInput') as HTMLInputElement | null;
const heightInput = document.getElementById('heightInput') as HTMLInputElement | null;
const angleInput = document.getElementById('angleInput') as HTMLInputElement | null;
const radiusInput = document.getElementById('radiusInput') as HTMLInputElement | null;
const radiusContainer = document.getElementById('radiusContainer') as HTMLElement | null;
const toolNameEl = document.getElementById('toolName');
const mousePositionEl = document.getElementById('mousePosition');

let isShiftPressed = false;

window.addEventListener('keydown', (event: KeyboardEvent) => { 
    if (event.key === 'Shift') isShiftPressed = true; 
});
window.addEventListener('keyup', (event: KeyboardEvent) => { 
    if (event.key === 'Shift') isShiftPressed = false; 
});

/**
 * @function updateBasicStatusBar
 * @description Updates the basic tool name and mouse X/Y coordinates in the status bar.
 * @returns {void}
 */
export function updateBasicStatusBar(): void {
    if (toolNameEl) {
        const currentToolName = (g.current_tool as any)?.name || (g.current_tool as any)?.id || 'Tool';
        toolNameEl.textContent = currentToolName;
    }
    if (mousePositionEl) {
        mousePositionEl.textContent = `X: ${Math.round(g.pX || 0)}, Y: ${Math.round(g.pY || 0)}`;
    }
}

/**
 * @function updateDrawingStatusBar
 * @description Updates the geometric information (coordinates, size, angle) in the status bar.
 * @param {MouseEvent} [event] - Optional mouse event to trigger coordinate calculations.
 * @returns {void}
 */
export function updateDrawingStatusBar(event?: MouseEvent): void {
    // [CRITICAL_LOGIC_START]
    const currentToolId = (g.current_tool as any)?.id || '';
    const isGeometricVectorTool = currentToolId.match(/Line|Circle|Rect|Ellipse|Rounded[-_]Rect/i);
    const selectionToolIds = [
        'btn-rect-select', 'btn-ellipse-select', 'btn-circle-select',
        'btn-single-row-select', 'btn-single-column-select', 'btn-lasso', 'btn-poly-select',
        'RectSelect', 'EllipseSelect', 'CircleSelect', 'Lasso', 'PolySelect'
    ];
    const isSelectionTool: boolean = selectionToolIds.some(id => currentToolId === id);
    const isSelectionGhostMode: boolean = isSelectionTool && (g as any).settings?.selection?.ghostMode && g.isSelectionActive;
    
    const vectorToolManager = (window as any).vectorToolManager;
    const isVectorEditing: boolean = !!vectorToolManager?.isEditing && !!vectorToolManager?.currentShape;
    const isVectorSelectTool: boolean = currentToolId === Tool.VectorSelect.id || currentToolId === 'btn-vector-select' || currentToolId.includes('VectorSelect');
    const isMoveContentTool: boolean = currentToolId === 'MoveContent' || currentToolId === 'btn-move-content';
    const isTransforming: boolean = g.isTransforming || (g as any).isTransforming;
    
    // Allow showing status bar only for geometric shape tools, selection tools, and move content
    const shouldShowStatusBar: boolean = isGeometricVectorTool || isSelectionTool || isSelectionGhostMode || isVectorEditing || isVectorSelectTool || isMoveContentTool || isTransforming;

    if (!g.drawing && !shouldShowStatusBar) {
        // Only hide if NOT in edit mode
        if (!isVectorEditing && !isSelectionGhostMode && !isMoveContentTool && !isTransforming) {
            const drawingActions = document.querySelector('.drawing-actions') as HTMLElement;
            if (drawingInfo) {
                drawingInfo.style.display = 'none';
                if (drawingActions) drawingActions.style.display = 'none';
            }
        }
        return;
    }

    if (drawingInfo && shouldShowStatusBar) {
        drawingInfo.style.display = 'flex';
    }

    const floatingContent = (g as any).floatingContent || (window as any).floatingContent;
    
    let startPoint = { x: g.startX, y: g.startY }; 
    let currentPoint = { x: g.pX, y: g.pY }; 

    // MoveContent special handling
    if (isMoveContentTool && floatingContent) {
        if (startXInput) startXInput.value = Math.round(floatingContent.x).toString();
        if (startYInput) startYInput.value = Math.round(floatingContent.y).toString();
        if (widthInput) widthInput.value = Math.round(floatingContent.canvas.width * (floatingContent.scaleX || 1)).toString();
        if (heightInput) heightInput.value = Math.round(floatingContent.canvas.height * (floatingContent.scaleY || 1)).toString();
        if (angleInput) angleInput.value = Math.round((floatingContent.angle || 0) * 180 / Math.PI).toString();
        return; // Skip standard sync
    }

    // Vector editing: drive fields from current shape, not toolId-derived mouse deltas.
    if (isVectorEditing) {
        const currentShape = vectorToolManager.currentShape as any;
        startPoint = { x: currentShape.x1, y: currentShape.y1 };
        currentPoint = { x: currentShape.x2, y: currentShape.y2 };
    }

    const isDraggingHandle: boolean = (g as any)._draggingCornerIndex >= 0 || (g as any)._draggingPointIndex >= 0;

    // Feature BUG: If editing existing ghost selection OR dragging a handle, use shape bounds instead of raw mouse
    if ((isSelectionGhostMode || isDraggingHandle) && (g as any)._selectionEditBounds) {
        const bounds = (g as any)._selectionEditBounds;
        startPoint = { x: bounds.x1, y: bounds.y1 };
        currentPoint = { x: bounds.x2, y: bounds.y2 };
    }

    let deltaX: number = currentPoint.x - startPoint.x;
    let deltaY: number = currentPoint.y - startPoint.y;
    
    // Circle snapping logic
    const isCircleSelectTool: boolean = currentToolId.includes('CircleSelect');
    if (isCircleSelectTool && g.drawing && !isSelectionGhostMode) {
        const squareSide: number = Math.max(Math.abs(deltaX), Math.abs(deltaY));
        deltaX = (deltaX < 0 ? -squareSide : squareSide);
        deltaY = (deltaY < 0 ? -squareSide : squareSide);
    }

    // [CRITICAL_LOGIC_END]
    
    syncInputs(deltaX, deltaY, startPoint, currentToolId, isVectorEditing);
}

/**
 * @function syncInputs
 * @description Internal helper to update input values.
 * @private
 */
function syncInputs(deltaX: number, deltaY: number, startPoint: { x: number, y: number }, currentToolId: string, isVectorEditing: boolean): void {
    const vectorToolManager = (window as any).vectorToolManager;
    const isEllipseTool: boolean = currentToolId.includes('Circle') || currentToolId.includes('Ellipse');
    const isLineTool: boolean = currentToolId.includes('Line');

    // STEP 3: Radius Logic
    if (radiusContainer && radiusInput) {
        const shouldShowRadius: boolean = isVectorEditing ? ((vectorToolManager.currentShape as any)?.type === 'circle') : isEllipseTool;
        if (shouldShowRadius) {
            radiusContainer.style.display = 'flex';
            radiusInput.value = Math.round(Math.hypot(deltaX, deltaY)).toString();
        } else {
            radiusContainer.style.display = 'none';
        }
    }

    // STEP 4: Angle Logic
    processAngle(deltaX, deltaY, isVectorEditing, isLineTool);

    // STEP 5: Coordinate Inputs
    if (startXInput) startXInput.value = Math.round(startPoint.x).toString();
    if (startYInput) startYInput.value = Math.round(startPoint.y).toString();
    if (widthInput) widthInput.value = Math.round(Math.abs(deltaX)).toString();
    if (heightInput) heightInput.value = Math.round(Math.abs(deltaY)).toString();

    // STEP 6: Input States
    const isSelectionGhostMode: boolean = (g.current_tool as any)?.id?.includes('Select') && (g as any).settings?.selection?.ghostMode && g.isSelectionActive;
    const isGeometricVectorTool = currentToolId.match(/Line|Circle|Rectangle|Ellipse|Rounded_Rectangle/i);
    const isMoveContentTool: boolean = currentToolId === 'MoveContent' || currentToolId === 'btn-move-content';
    const shouldDisableInputs: boolean = !isGeometricVectorTool && !isSelectionGhostMode && !isVectorEditing && !isMoveContentTool;
    
    [startXInput, startYInput, widthInput, heightInput, angleInput, radiusInput].forEach(element => {
        if (element) element.disabled = shouldDisableInputs;
    });
}

/**
 * @function processAngle
 * @description Internal logic for angle processing.
 * @private
 */
function processAngle(deltaX: number, deltaY: number, isVectorEditing: boolean, isLineTool: boolean): void {
    const vectorToolManager = (window as any).vectorToolManager;
    if (isVectorEditing && angleInput) {
        const currentShape = vectorToolManager.currentShape as any;
        if (currentShape.type === 'line') {
            let angleDeg: number = Math.atan2(currentShape.y2 - currentShape.y1, currentShape.x2 - currentShape.x1) * 180 / Math.PI;
            if (angleDeg < 0) angleDeg += 360;
            angleInput.value = Math.round(angleDeg).toString();
        } else {
            angleInput.value = Math.round(currentShape.style?.angle || 0).toString();
        }
    } else if (isShiftPressed && isLineTool) {
        const rawAngle: number = Math.atan2(deltaY, deltaX) * 180 / Math.PI;
        const allowedSnaps: number[] = [0, 30, 45, 60, 90, 120, 135, 150, 180, -30, -45, -60, -90, -120, -135, -150, -180];
        const snappedAngle: number = allowedSnaps.reduce((prev, curr) => Math.abs(curr - rawAngle) < Math.abs(prev - rawAngle) ? curr : prev);
        if (angleInput) angleInput.value = Math.round(snappedAngle).toString();
    } else if (angleInput) {
        let rawAngle: number = Math.atan2(deltaY, deltaX) * 180 / Math.PI;
        if (rawAngle < 0) rawAngle += 360;
        angleInput.value = Math.round(rawAngle).toString();
    }
}

/**
 * @function initStatusBarEvents
 * @description Initializes input events for the status bar.
 * @returns {void}
 */
export function initStatusBarEvents(): void {
    const handleInputChange = () => {
        const vectorToolManager = (window as any).vectorToolManager;
        const currentToolId = (g.current_tool as any)?.id;
        const isVectorEditing: boolean = !!vectorToolManager?.isEditing;
        const isSelectionGhostMode: boolean = g.isSelectionActive && (g as any).settings?.selection?.ghostMode;

        const isMoveContentTool: boolean = currentToolId === 'MoveContent' || currentToolId === 'btn-move-content';

        if (!isVectorEditing && !isSelectionGhostMode && !isMoveContentTool) {
            return;
        }

        const newX1: number = parseFloat(startXInput?.value || '0');
        const newY1: number = parseFloat(startYInput?.value || '0');
        const newWidth: number = parseFloat(widthInput?.value || '0');
        const newHeight: number = parseFloat(heightInput?.value || '0');
        const newRadius: number = parseFloat(radiusInput?.value || '0');
        const newAngle: number = parseFloat(angleInput?.value || '0');

        if (isVectorEditing && vectorToolManager.currentShape) {
            vectorToolManager.updateShapeGeometry({}, {
                x1: newX1,
                y1: newY1,
                x2: newX1 + newWidth,
                y2: newY1 + newHeight,
                radius: newRadius,
                angle: newAngle
            });
        } else if (isSelectionGhostMode) {
            const isCircleSelectTool: boolean = currentToolId.includes('CircleSelect');
            let finalWidth: number = newWidth;
            let finalHeight: number = newHeight;
            
            if (isCircleSelectTool) {
                const currentRadius: number = parseFloat(radiusInput?.value || '0');
                if (currentRadius * 2 !== newWidth && currentRadius > 0) {
                    finalWidth = currentRadius * 2;
                    finalHeight = finalWidth;
                } else {
                    finalWidth = Math.max(newWidth, newHeight);
                    finalHeight = finalWidth;
                }
                
                if (widthInput) widthInput.value = Math.round(finalWidth).toString();
                if (heightInput) heightInput.value = Math.round(finalHeight).toString();
                if (radiusInput) radiusInput.value = Math.round(finalWidth / 2).toString();
            }

            const bounds = { x1: newX1, y1: newY1, x2: newX1 + finalWidth, y2: newY1 + finalHeight };
            (g as any)._selectionEditBounds = bounds;
            
            (g as any).startX = bounds.x1;
            (g as any).startY = bounds.y1;
            (g as any).pX = bounds.x2;
            (g as any).pY = bounds.y2;

            const selectionMode = ((g as any)._lastSelectionMode || 'replace') as any;
            const isRectSelect: boolean = currentToolId.includes('RectSelect');
            const isEllipseSelect: boolean = currentToolId.includes('EllipseSelect');

            if (isRectSelect) {
                import('@hcie/tools').then(tools => tools.buildRectSelection(bounds.x1, bounds.y1, bounds.x2, bounds.y2, selectionMode));
            } else if (isEllipseSelect) {
                import('@hcie/tools').then(tools => tools.buildEllipseSelection(bounds.x1, bounds.y1, bounds.x2, bounds.y2, selectionMode));
            } else if (isCircleSelectTool) {
                import('@hcie/tools').then(tools => tools.buildCircleSelection(bounds.x1, bounds.y1, bounds.x2, bounds.y2, selectionMode));
            }
            EventBus.emit('RENDER_LAYERS');
        } else if (isMoveContentTool) {
            const floatingContent = (g as any).floatingContent || (window as any).floatingContent;
            if (floatingContent) {
                floatingContent.x = newX1;
                floatingContent.y = newY1;
                
                // Approximate scale back from width/height
                if (floatingContent.canvas.width > 0) {
                    floatingContent.scaleX = newWidth / floatingContent.canvas.width;
                }
                if (floatingContent.canvas.height > 0) {
                    floatingContent.scaleY = newHeight / floatingContent.canvas.height;
                }
                
                floatingContent.angle = newAngle * Math.PI / 180;
                
                const transformer = (window as any).transformUI;
                if (transformer && typeof transformer.updatePosition === 'function') {
                    // Update internal transformer state to match
                    transformer.angle = floatingContent.angle;
                    transformer.scaleX = floatingContent.scaleX;
                    transformer.scaleY = floatingContent.scaleY;
                    transformer.updatePosition();
                }
                
                EventBus.emit('RENDER_LAYERS');
            }
        }
    };

    [startXInput, startYInput, widthInput, heightInput, angleInput, radiusInput].forEach(element => {
        if (element) {
            element.addEventListener('input', handleInputChange);
            element.addEventListener('change', handleInputChange);
            element.addEventListener('keydown', (event: KeyboardEvent) => {
                if (event.key === 'Enter') {
                    handleInputChange();
                    (window as any).commitDrawing?.();
                }
                if (event.key === 'Escape') {
                    (window as any).cancelDrawing?.();
                }
            });
        }
    });
}
